import React, { useState, useEffect } from 'react';
import { getClientWorkouts } from '../services/api';
import { useI18n } from '../i18n/context';
import { getIntlLocaleTag } from '../lib/localeRuntime';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, CalendarX, History, Calendar } from 'lucide-react';
import WorkoutDetailModal from './WorkoutDetailModal';

const ClientMain = ({ currentUser, userContext }) => {
  const { t, locale } = useI18n();
  const nFormatLocale = getIntlLocaleTag(locale);
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Get local date string (YYYY-MM-DD)
  const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [showWorkoutDetail, setShowWorkoutDetail] = useState(false);

  useEffect(() => {
    if (currentUser?.id) {
      loadClientData();
    }
  }, [currentUser?.id]);

  const loadClientData = async () => {
    if (!currentUser?.id) return;
    
    setLoading(true);
    try {
      const workoutsResponse = await getClientWorkouts(currentUser.id);
      setWorkouts(workoutsResponse.data);
    } catch (error) {
      console.error('Error loading client data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Фильтруем тренировки на выбранную дату
  const selectedDateWorkouts = workouts.filter(w => w.date === selectedDate);
  
  // Сортируем по времени
  const sortedWorkouts = selectedDateWorkouts.sort((a, b) => a.time.localeCompare(b.time));

  const formatDate = (dateString) => {
    // Parse date components explicitly to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(nFormatLocale);
  };

  const handleWorkoutClick = (workout) => {
    setSelectedWorkout(workout);
    setShowWorkoutDetail(true);
  };

  const handleWorkoutUpdate = () => {
    // Перезагружаем данные тренировок
    loadClientData();
  };

  if (loading) {
    return (
      <div className="container mx-auto w-full min-w-0 max-w-full overflow-x-hidden px-3 py-6 sm:px-4">
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="sr-only">{t('clientDash.loadingSr')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto w-full min-w-0 max-w-full overflow-x-hidden px-3 py-6 pb-8 sm:px-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h2 className="text-2xl font-bold mb-4 sm:mb-0">{t('clientDash.myWorkouts')}</h2>
        
        {/* Выбор даты */}
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-muted-foreground" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
          />
        </div>
      </div>

      {/* Тренировки на выбранную дату */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">
          {t('clientDash.workoutsOn', { date: formatDate(selectedDate) })}
        </h3>
        
        {sortedWorkouts.length === 0 ? (
          <Card className="text-center py-8">
            <CardContent>
              <CalendarX className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
              <p className="text-muted-foreground">{t('clientDash.noWorkouts')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sortedWorkouts.map(workout => (
              <Card key={workout.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleWorkoutClick(workout)}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="font-medium text-lg">{workout.time}{workout.end_time && ` - ${workout.end_time}`}</h5>
                    <Badge variant="default">{t('payments.status.workout')}</Badge>
                  </div>
                  {workout.comment && (
                    <p className="text-muted-foreground text-sm mb-2">{workout.comment}</p>
                  )}
                  <p className="text-sm">
                    <span className="font-medium">{t('clientDash.exercises')}</span>{' '}
                    {workout.exercises?.length || 0}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Модальное окно деталей тренировки */}
      {showWorkoutDetail && selectedWorkout && (
        <WorkoutDetailModal
          show={showWorkoutDetail}
          onHide={() => {
            setShowWorkoutDetail(false);
            setSelectedWorkout(null);
          }}
          workout={selectedWorkout}
          onWorkoutUpdate={handleWorkoutUpdate}
          onAddExercise={handleWorkoutUpdate}
          onAddNote={handleWorkoutUpdate}
          onExerciseUpdate={handleWorkoutUpdate}
        />
      )}
    </div>
  );
};

export default ClientMain;
